import { FC, useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'

import type { Whitelisting } from '@img-arena/consoles-api-streaming'
import { DataCard } from '@/components/card'
import { OpenDialogButton } from '@/components/ConsoleDialog/handlers'
import WhitelistingForm from '@/components/forms/WhitelistingForm'
import { ActionDeleteItem, ActionsMenu } from '@/components/table/actions'
import { FilterableTable } from '@/components/table/base'
import { CheckboxMenu } from '@/components/table/checkbox'
import { CheckboxDeleteSelectedItem } from '@/components/table/checkbox/CheckboxDeleteSelectedItem'
import { TitleSetter } from '@/components/Title'
import { STORE_STATUS } from '@/constants/store'
import { createBulkHandler } from '@/service/bulkOperationsService'
import { streamingRestApi } from '@/service/streamingApi'
import { useWhitelistingStore } from '@/stores/whitelisting'

import { WHITELISTING_TABLE_COLUMNS, WHITELISTING_TABLE_FILTERS } from './whitelisting.table'

const tableId = 'dge-whitelisting-table'

const WhitelistingView: FC = () => {
  const { data, isFetching, isLoading } = useWhitelistingStore(
    useShallow((state) => ({
      data: state.data,
      isLoading: state.status === STORE_STATUS.loading,
      isFetching: state.status === STORE_STATUS.fetching
    }))
  )

  const { fetchData, resetState } = useWhitelistingStore(
    useShallow((state) => ({
      fetchData: state.fetchData,
      resetState: state.resetState
    }))
  )

  useEffect(() => {
    fetchData()
    return resetState
  }, [])

  const handleDeleteIP = async (whitelisting: Whitelisting) => {
    await streamingRestApi.endpoints.whitelist.deleteWhitelistIP(whitelisting.id)
    await fetchData()
  }

  const handleDeleteSelectedWhitelistIPs = createBulkHandler({
    name: 'Delete IPs',
    operation: async (whitelisting: Whitelisting) => {
      await streamingRestApi.endpoints.whitelist.deleteWhitelistIP(whitelisting.id)
    },
    refreshData: fetchData,
    getItemDisplayName: (whitelisting) => `${whitelisting.cidrRange} (ID: ${whitelisting.id})`
  })

  return (
    <>
      <TitleSetter title="Whitelisting" />
      <DataCard
        title="Whitelisting"
        headerPostfixActions={
          <>
            <CheckboxMenu tableId={tableId}>
              <CheckboxDeleteSelectedItem
                onDelete={handleDeleteSelectedWhitelistIPs}
                renderItem={(row) => `${row.id} (${row.cidrRange})`}
                successMessage={(rows) => `${rows.length} IPs deleted successfully`}
                errorMessage={(rows) => `Failed to delete ${rows.length} IPs`}
                confirmationTitle="Delete Selected IPs"
                confirmationMessage="Are you sure you want to delete the following IPs? This action cannot be undone."
              />
            </CheckboxMenu>
            <OpenDialogButton label="Add Subscriber">
              <WhitelistingForm onSubmit={fetchData} />
            </OpenDialogButton>
          </>
        }
      >
        <FilterableTable
          data={data}
          tableId={tableId}
          loading={isLoading}
          fetching={isFetching}
          filters={WHITELISTING_TABLE_FILTERS}
          columns={WHITELISTING_TABLE_COLUMNS}
          clientSideSortAndPagination
          clientSideFilters
          withCheckboxes
          rowActions={
            <ActionsMenu>
              <ActionDeleteItem
                displaySeparately
                onDelete={handleDeleteIP}
                successMessage={(row) => `IP ${row.cidrRange} deleted successfully`}
                errorMessage={(row) => `Failed to delete IP ${row.cidrRange}`}
                confirmationMessage={(row) =>
                  `Are you sure you want to delete IP ${row.cidrRange}?`
                }
              />
            </ActionsMenu>
          }
        />
      </DataCard>
    </>
  )
}

export default WhitelistingView
